import type {
  DevAutoCalcLogDto,
  DevAutoCalcLogFilter,
  DevAutoCalcRunDto,
} from '@/api';
import { compareNumericValues } from '@/components/table/table-helpers';
import { formatDateTimeDisplay } from '@/utils/date-utils';
import type { ColumnFilter, FilterOption } from './price-row-filters';

export const AUTOCALC_FILTER_COLUMNS = [
  'priceUnitId',
  'productId',
  'category',
  'reason',
] as const;
export type AutoCalcFilterColumn = (typeof AUTOCALC_FILTER_COLUMNS)[number];

/** Dropdown options for the runs: the run date without seconds. */
export const toRunOptions = (
  runs: readonly DevAutoCalcRunDto[],
): FilterOption[] =>
  runs.map((run) => ({
    id: run.runKey,
    label: formatDateTimeDisplay(run.runDate),
  }));

/**
 * Keeps the selected run while it exists. Otherwise selects the newest run (the runs are newest first),
 * so a run is always selected when there is one.
 */
export const resolveSelectedRun = (
  runs: readonly DevAutoCalcRunDto[],
  selectedRunKey: string | undefined,
): string | undefined =>
  runs.some((run) => run.runKey === selectedRunKey)
    ? selectedRunKey
    : runs[0]?.runKey;

const selectedValues = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === 'string')
    : [];

/** Converts the table column filters of the selected run to the API filter. */
export const toAutoCalcLogFilter = (
  runKey: string,
  columnFilters: readonly ColumnFilter[],
): DevAutoCalcLogFilter => {
  const filter: DevAutoCalcLogFilter = { runKey };
  for (const { id, value } of columnFilters) {
    const values = selectedValues(value);
    if (values.length === 0) continue;
    if (id === 'priceUnitId') filter.priceUnitIds = values.map(Number);
    if (id === 'productId') filter.productIds = values.map(Number);
    if (id === 'category') filter.categories = values;
    if (id === 'reason') filter.reasons = values;
  }
  return filter;
};

/** Filter options: the values of the column in the logs of the run. */
export const getAutoCalcColumnOptions = (
  column: AutoCalcFilterColumn,
  logs: readonly DevAutoCalcLogDto[],
): FilterOption[] =>
  [
    ...new Set(
      logs
        .map((log) => log[column])
        .filter((v): v is string | number => v != null)
        .map(String),
    ),
  ]
    .sort(
      column === 'reason' || column === 'category'
        ? undefined
        : compareNumericValues,
    )
    .map((id) => ({ id, label: id }));

/** The number of columns whose filter has a selection. */
export const countAutoCalcFilters = (filter: DevAutoCalcLogFilter): number =>
  [
    filter.priceUnitIds,
    filter.productIds,
    filter.categories,
    filter.reasons,
  ].filter((values) => values !== undefined).length;
